import { ReactNode, useMemo } from "react"
import { Boost } from "@mezo/api"
import { SupportedTokenName } from "@mezo/sdk"
import {
  Block,
  ParagraphSmall,
  Tooltip,
  useStyletron,
} from "@mezo-org/mezo-clay"

import TooltipLabel from "#/shared/components/TooltipLabel"
import { SHOW_DEPOSIT_INFO } from "#/shared/constants"
import { DepositBoxProps, useDepositBox } from "#/shared/hooks"
import { DepositMigrationStatus } from "#/shared/types"
import {
  calculateLockDuration,
  formatDate,
  formatMats,
  getCurrencySymbol,
} from "#/shared/utils"

import DepositBoxHeader from "./DepositBoxHeader"
import DepositBoxInfo from "./DepositBoxInfo"

type DepositBoxComponentProps = Omit<DepositBoxProps, "currentLockDuration"> & {
  totalDailyMats: number
  dailyBoostMats: Boost[]
  migrationStatus?: DepositMigrationStatus
}

type DepositBoxWrapperProps = {
  tokenName: SupportedTokenName
  children: ReactNode
}

function DepositBoxWrapper({ tokenName, children }: DepositBoxWrapperProps) {
  const isWBTCDeposit = tokenName === "WBTC"

  const content = isWBTCDeposit
    ? `${getCurrencySymbol(
        "WBTC",
      )} assets will be bridged as ${getCurrencySymbol("tBTC")} on Mezo.`
    : "Enables your assets to be bridged automatically to mainnet on launch."

  return (
    <Tooltip
      content={content}
      position="top"
      overrides={{ Body: { style: () => ({ zIndex: 101 }) } }}
    >
      {children}
    </Tooltip>
  )

  return children
}

export default function DepositBox({
  id,
  depositId,
  userAmount,
  amount,
  token,
  lockStart,
  lockEnd,
  unlockTime,
  dailyMats,
  totalDailyMats,
  mintedAmount,
  dailyBoostMats,
  migrationStatus = DepositMigrationStatus.NotEligible,
}: DepositBoxComponentProps) {
  const [css, theme] = useStyletron()

  const currentLockDuration = calculateLockDuration(
    new Date(lockStart),
    new Date(lockEnd),
  )

  const { openBoostModal, openWithdrawalModal, openReturnStbtcModal } =
    useDepositBox({
      id,
      depositId,
      amount,
      token,
      dailyMats,
    })

  const formattedTotalDailyMats = formatMats(totalDailyMats)

  const tokenName = useMemo(
    () =>
      migrationStatus === DepositMigrationStatus.Completed ? "tBTC" : token,
    [migrationStatus, token],
  )

  const depositInfo = (
    <Block>
      <ParagraphSmall marginBottom="4px">
        This tooltip shouldn&apos;t be visible in production
      </ParagraphSmall>
      <ParagraphSmall>Deposit ID: {depositId}</ParagraphSmall>
      <ParagraphSmall>
        Lock Start: {formatDate(new Date(`${lockStart}Z`))}
      </ParagraphSmall>
      <ParagraphSmall>
        Lock End: {formatDate(new Date(`${lockEnd}Z`))}
      </ParagraphSmall>
    </Block>
  )

  const depositBox = (
    <Block
      className={css({
        overflow: "hidden",
        borderRadius: theme.borders.radius400,
      })}
    >
      <DepositBoxHeader
        depositId={depositId}
        id={id}
        unlockTime={unlockTime}
        dailyMats={formattedTotalDailyMats}
        lockDuration={currentLockDuration}
        dailyBoostMats={dailyBoostMats}
        onBoostButtonClick={openBoostModal}
      />
      <DepositBoxInfo
        depositId={depositId}
        id={id}
        token={tokenName}
        userAmount={userAmount}
        unlockTime={unlockTime}
        onWithdrawalButtonClick={openWithdrawalModal}
        onReturnButtonClick={openReturnStbtcModal}
        mintedAmount={mintedAmount}
        migrationStatus={migrationStatus}
      />
    </Block>
  )

  if (SHOW_DEPOSIT_INFO) {
    return (
      <TooltipLabel value={depositInfo} variant="light" placement="bottom">
        <Block>
          <DepositBoxWrapper tokenName={tokenName}>
            {depositBox}
          </DepositBoxWrapper>
        </Block>
      </TooltipLabel>
    )
  }

  return (
    <DepositBoxWrapper tokenName={tokenName}>{depositBox}</DepositBoxWrapper>
  )
}
